// The signed-in session lives in one of two stores: a normal login keeps it in
// sessionStorage, while "Remember me" (see LoginPage) persists it in
// localStorage so it survives a browser restart. Every reader therefore has to
// check both, and every writer has to target whichever store holds the live
// session — getting that wrong is how a remembered user ends up with a blank
// name pill or a request to /upload-photo/null.
//
// Use these helpers instead of touching sessionStorage/localStorage directly.

export const AUTH_KEYS = [
  "authToken",
  "userRole",
  "fullName",
  "userID",
  "profileImageURL",
];

// The store backing the current session, or null when nobody is signed in.
export function activeStore() {
  if (sessionStorage.getItem("authToken")) return sessionStorage;
  if (localStorage.getItem("authToken")) return localStorage;
  return null;
}

// Read a session value. sessionStorage wins when both stores hold the key.
export function getAuthItem(key) {
  const stored = sessionStorage.getItem(key);
  return stored === null ? localStorage.getItem(key) : stored;
}

// Bearer token for manual axios calls. customAxios adds this automatically.
export function getAuthToken() {
  return getAuthItem("authToken") || "";
}

// Update one value in place, without migrating the session to the other store.
export function setAuthItem(key, value) {
  const store = activeStore();
  if (store) store.setItem(key, value);
}

// Begin a new session. Both stores are cleared first so that a value left over
// from an earlier login can't linger in the other store and leak into
// getAuthItem for a key the new session doesn't set.
export function startSession(values, { remember = false } = {}) {
  clearAuth();
  const store = remember ? localStorage : sessionStorage;
  Object.keys(values).forEach((key) => {
    store.setItem(key, values[key]);
  });
}

// Drop the session from both stores — used on sign-out and on a 401.
export function clearAuth() {
  AUTH_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
}
